import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/about", label: "About Us", testId: "nav-about-link" },
  { to: "/businesses", label: "Our Businesses", testId: "nav-businesses-link" },
  { to: "/approach", label: "Our Approach", testId: "nav-approach-link" },
];

export const Logo = () => (
  <Link to="/" data-testid="nav-home-link" className="flex items-baseline gap-2 group">
    <span className="font-display text-xl sm:text-2xl tracking-tight text-white">
      Touson<span className="text-gold">.</span>
    </span>
    <span className="font-mono text-[10px] tracking-[0.35em] text-white/50 group-hover:text-gold transition-colors duration-300">
      VENTURES
    </span>
  </Link>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-navy/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-10">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className={`font-mono text-[11px] tracking-[0.25em] uppercase transition-colors duration-300 ${
                  location.pathname === l.to ? "text-gold" : "text-white/60 hover:text-white"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-contact-button"
              className="group flex items-center gap-2 border border-gold/60 text-gold font-mono text-[11px] tracking-[0.25em] uppercase px-5 py-2.5 rounded-full hover:bg-gold hover:text-navy transition-colors duration-300"
            >
              Contact Us
              <ArrowUpRight size={13} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </Link>
          </nav>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-navy/95 backdrop-blur-2xl lg:hidden flex flex-col justify-center px-8"
            data-testid="nav-mobile-menu"
          >
            <nav className="flex flex-col gap-6">
              {[...LINKS, { to: "/contact", label: "Contact Us", testId: "nav-mobile-contact-link" }].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={l.to}
                    data-testid={l.testId}
                    className="font-display text-4xl text-white hover:text-gold transition-colors duration-300"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <p className="mt-16 font-mono text-[10px] tracking-[0.3em] text-white/40 uppercase">
              Touson Ventures (OPC) Private Limited
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
